'use client'

import { memo } from 'react'
import dynamic from 'next/dynamic'
import { useSession } from 'next-auth/react'

import {
  IS_SYNERISE_PERSONALIZED_RECOMMENDATION_ENABLED,
  useSyneriseUUID
} from 'lib/synerise'
import { CategoryCard, ProductTypes, UpsellingSections } from 'modules/products'
import LazyLoader from 'shared/ui/LazyLoader/LazyLoader'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import { BannerTypes } from '../../submodules/banners/api'
import { filterAndSortBannersWithAuth } from '../../submodules/banners/utils'

import BannersSection from './components/BannersSection'
import BestsellersSection from './components/BestsellersSection'
import LandingMobileButtonsSection from './components/LandingMobileButtonsSection'

import styles from './LandingPage.module.scss'

const CategoriesSection = dynamic(
  () => import('./components/CategoriesSection'),
  { ssr: false }
)
const DescriptionSection = dynamic(
  () => import('./components/DescriptionSection'),
  { ssr: false }
)
const FeaturedSection = dynamic(
  () => import('./components/FeaturedSection'),
  { ssr: false }
)
const HowItWorksSection = dynamic(
  () => import('./components/HowItWorksSection'),
  { ssr: false }
)
const MostFrequentlyBoughtProducts = dynamic(
  () => import('./components/MostFrequentlyBoughtSection'),
  { ssr: false }
)
const NewsletterSection = dynamic(
  () => import('./components/NewsletterSection'),
  { ssr: false }
)
const RecentlyAddedSection = dynamic(
  () => import('./components/RecentlyAddedSection'),
  { ssr: false }
)
const RecommendedSection = dynamic(
  () => import('./components/RecommendedSection'),
  { ssr: false }
)

const PRODUCT_SLIDER_HEIGHT = 564
const FEATURED_BANNERS_HEIGHT = 208
const FEATURED_BANNERS_MOBILE_HEIGHT = 569
const CATEGORIES_HEIGHT = 240
const HOW_IT_WORKS_HEIGHT = 505
const HOW_IT_WORKS_MOBILE_HEIGHT = 1069
const NEWSLETTER_HEIGHT = 474
const NEWSLETTER_MOBILE_HEIGHT = 614
const DESCRIPTION_HEIGHT = 74
const DESCRIPTION_MOBILE_HEIGHT = 99

type LandingPageProps = {
  banners: BannerTypes[]
  upselling: UpsellingSections
  categories: CategoryCard[]
  mostFrequentlyBought: ProductTypes[]
  distributionCenterId: string
}

const LandingPage = memo(
  ({
    banners,
    upselling,
    categories,
    mostFrequentlyBought,
    distributionCenterId
  }: LandingPageProps) => {
    const { data: session } = useSession()
    const filteredBanners = filterAndSortBannersWithAuth(banners, session)

    const filteredMainPageBanners = filteredBanners?.filter(
      banner => banner?.productCategoryId === null
    )

    const topSectionBanners = (filteredMainPageBanners || []).filter(
      banner => banner.section === 'main'
    )

    const tileBanners = (filteredMainPageBanners || [])
      .filter(banner => banner.section === 'first_additional')
      .slice(0, 3)

    const { syneriseClientUUID } = useSyneriseUUID()

    return (
      <>
        <LandingMobileButtonsSection />
        <BannersSection banners={topSectionBanners} />
        <BestsellersSection products={upselling?.bestsellers || []} />
        {IS_SYNERISE_PERSONALIZED_RECOMMENDATION_ENABLED && (
          <LazyLoader height={PRODUCT_SLIDER_HEIGHT}>
            {syneriseClientUUID && (
              <RecommendedSection
                syneriseClientUUID={syneriseClientUUID}
                distributionCenterId={distributionCenterId}
              />
            )}
          </LazyLoader>
        )}
        <LazyLoader
          height={FEATURED_BANNERS_HEIGHT}
          mobileHeight={FEATURED_BANNERS_MOBILE_HEIGHT}
        >
          <FeaturedSection
            banners={tileBanners}
            placement='main_page_featured'
          />
        </LazyLoader>
        <LazyLoader height={CATEGORIES_HEIGHT}>
          <CategoriesSection categories={categories} />
        </LazyLoader>
        <LazyLoader
          height={HOW_IT_WORKS_HEIGHT}
          mobileHeight={HOW_IT_WORKS_MOBILE_HEIGHT}
        >
          <HowItWorksSection />
        </LazyLoader>
        <div className={styles.mobileDivider} />
        <LazyLoader height={PRODUCT_SLIDER_HEIGHT}>
          <RecentlyAddedSection products={upselling?.newest || []} />
        </LazyLoader>
        {isNotNilOrEmpty(mostFrequentlyBought) && (
          <LazyLoader height={PRODUCT_SLIDER_HEIGHT}>
            <MostFrequentlyBoughtProducts products={mostFrequentlyBought} />
          </LazyLoader>
        )}
        <LazyLoader
          id='newsletter'
          height={NEWSLETTER_HEIGHT}
          mobileHeight={NEWSLETTER_MOBILE_HEIGHT}
        >
          <NewsletterSection />
        </LazyLoader>
        <LazyLoader
          height={DESCRIPTION_HEIGHT}
          mobileHeight={DESCRIPTION_MOBILE_HEIGHT}
        >
          <DescriptionSection />
        </LazyLoader>
      </>
    )
  }
)

export default LandingPage
